import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { LabelDark } from "@/components/site/ui";
import { BrowserFrame, SCREEN_IMAGES } from "@/components/site/mockups";

const EASE = [0.16, 1, 0.3, 1];

/* Compact navy hero for inner marketing pages — matches the homepage system. */
export default function PageHero({ eyebrow, title, subtitle, screenKey, screenComp: ScreenComp, primary, secondary }) {
  const realImg = screenKey ? SCREEN_IMAGES[screenKey] : null;
  const withVisual = Boolean(ScreenComp || realImg);
  return (
    <section className="relative bg-slate-900 text-white overflow-hidden">
      <div className="absolute inset-0 ayo-grid-bg-dark opacity-60" />
      <div className="absolute -top-40 -right-40 w-[34rem] h-[34rem] rounded-full bg-teal-500/20 blur-[120px]" />
      <div className="absolute top-40 -left-40 w-[28rem] h-[28rem] rounded-full bg-emerald-500/15 blur-[120px]" />
      <div className={`relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-16 sm:pt-40 sm:pb-24 ${withVisual ? "grid lg:grid-cols-[1.05fr_1fr] gap-12 items-center" : "max-w-4xl"}`}>
        <div>
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            {eyebrow && <LabelDark>{eyebrow}</LabelDark>}
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE, delay: 0.1 }}
            className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-6"
          >
            {title}
          </motion.h1>
          {subtitle && (
            <motion.p
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.25 }}
              className="mt-6 text-lg sm:text-xl text-slate-300 max-w-xl leading-relaxed"
            >
              {subtitle}
            </motion.p>
          )}
          {(primary || secondary) && (
            <motion.div
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.4 }}
              className="mt-8 flex flex-col sm:flex-row gap-3"
            >
              {primary && (
                <Link to={primary.to} data-testid="pagehero-primary" className="inline-flex items-center justify-center gap-2 text-sm font-semibold text-white bg-gradient-to-r from-teal-500 to-emerald-500 px-7 py-4 rounded-full hover:shadow-xl hover:shadow-emerald-500/30 hover:scale-[1.03] active:scale-[0.98] transition-all">
                  {primary.label} <ArrowRight className="w-4 h-4" />
                </Link>
              )}
              {secondary && (
                <Link to={secondary.to} data-testid="pagehero-secondary" className="inline-flex items-center justify-center gap-2 text-sm font-semibold text-white bg-white/5 border border-white/15 px-7 py-4 rounded-full hover:bg-white/10 transition-all">
                  {secondary.label} <ArrowUpRight className="w-4 h-4" />
                </Link>
              )}
            </motion.div>
          )}
        </div>
        {withVisual && (
          <motion.div initial={{ opacity: 0, y: 40, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.9, ease: EASE, delay: 0.35 }}>
            <BrowserFrame image={realImg?.src} alt={realImg?.alt}>{ScreenComp ? <ScreenComp /> : null}</BrowserFrame>
          </motion.div>
        )}
      </div>
    </section>
  );
}
